import {
  BreakPointCondition,
  UI,
  useBreakpointIf,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { Typography } from '@genial/design-tokens';
import { InteractionChannelAuditRequest } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { Box } from 'native-base';
import React, { useEffect, useState } from 'react';

import { useOrderOriginRequired } from '../../../../hooks/useOrderOriginRequired';
import { ProductsType } from '../../../BatchTicket/components/DynamicAsset/constants';

import { AgreementCheck } from './components/AgreementCheck';
import { ChannelSelection } from './components/ChannelSelection';
import { DateSelection } from './components/DateSelection';
import { TimeSelection } from './components/TimeSelection';
import { getDefaultValuesFromInteractionChannel } from './constants';

export interface ProveOrderOriginProps {
  productTypeId?: ProductsType;
  handleUpdateInteractionChannelAudit: (data?: InteractionChannelAuditRequest | null) => void;
  CustomHandleElement?: React.ReactNode;
  hideSeparator?: boolean;
  forceMobile?: boolean;
  defaultInteractionChannel?: InteractionChannelAuditRequest;
  hideDateTimeFields?: boolean;
  forceOrderOriginRequired?: boolean;
}

export const ProveOrderOrigin: React.FC<ProveOrderOriginProps> = ({
  productTypeId,
  handleUpdateInteractionChannelAudit,
  CustomHandleElement,
  hideSeparator,
  forceMobile,
  defaultInteractionChannel,
  hideDateTimeFields,
  forceOrderOriginRequired = false,
}) => {
  const defaultValues = getDefaultValuesFromInteractionChannel(defaultInteractionChannel);
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const [isOrderOriginRequired, setIsOrderOriginRequired] = useState<boolean>();
  const [agreementChecked, setAgreementChecked] = useState(defaultValues.agreement);
  const [channelSelected, setChannelSelected] = useState(defaultValues.channelSelected);
  const [channelAuditSelected, setChannelAuditSelected] = useState(
    defaultValues.channelAuditSelected
  );
  const [dateSelected, setDateSelected] = useState(defaultValues.dateSelected);
  const [timeSelected, setTimeSelected] = useState(defaultValues.timeSelected);

  const { verifyProduct } = useOrderOriginRequired();

  useEffect(() => {
    if (!productTypeId || forceOrderOriginRequired) {
      return setIsOrderOriginRequired(true);
    }
    setIsOrderOriginRequired(verifyProduct(productTypeId));
  }, [verifyProduct, productTypeId, forceOrderOriginRequired]);

  useEffect(() => {
    if (isOrderOriginRequired === false) {
      handleUpdateInteractionChannelAudit(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrderOriginRequired]);

  useEffect(() => {
    const fieldsAreBlank = hideDateTimeFields
      ? !agreementChecked || !channelSelected || !channelAuditSelected
      : !agreementChecked ||
        !channelSelected ||
        !dateSelected ||
        !channelAuditSelected ||
        !timeSelected;

    if (fieldsAreBlank) {
      return handleUpdateInteractionChannelAudit(undefined);
    }

    let dateWithTime: Date | undefined;

    if (hideDateTimeFields) {
      dateWithTime = new Date();
    } else if (dateSelected && timeSelected) {
      const [hour, minute] = timeSelected.split(':').map(Number);
      dateWithTime = new Date(dateSelected);
      dateWithTime.setHours(hour, minute, 0, 0);

      if (Utils.Date.IsFuture(dateWithTime, true)) {
        return handleUpdateInteractionChannelAudit(undefined);
      }
    }

    handleUpdateInteractionChannelAudit({
      interactionChannelAgreement: agreementChecked,
      interactionChannelAuditId: channelAuditSelected!,
      interactionChannelOptionValue: channelSelected,
      interactionChannelDate: dateWithTime,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementChecked, channelSelected, channelAuditSelected, dateSelected, timeSelected]);

  if (!isOrderOriginRequired) {
    return null;
  }

  return (
    <VStack pb={hideSeparator ? undefined : 'inline.xxxs'} zIndex={5}>
      <Stack
        justifyContent="space-between"
        zIndex={10}
        direction={forceMobile ? 'column' : { base: 'column', lg: 'row' }}
        alignItems={forceMobile ? 'inherit' : { base: 'inherit', lg: 'center' }}>
        <HStack
          space="inline.nano"
          alignItems="center"
          pb={forceMobile ? 'stack.xxxs' : { base: 'stack.xxxs', lg: 0 }}>
          <UI.Icon name="Warning" size="xs" color="feedback.attention.default" />
          <VStack>
            <UI.Text variant="semibold">Comprove a origem da ordem</UI.Text>
            <UI.Text color="neutral.dark" fontSize={Typography['fontsize.xs']}>
              Antes de enviar, informe a origem da boleta
            </UI.Text>
          </VStack>
        </HStack>

        <Stack
          space={
            forceMobile
              ? 'stack.xxxs'
              : {
                  base: 'stack.xxxs',
                  md: 'inline.quarck',
                  lg: 'inline.xxxs',
                }
          }
          zIndex={10}
          flex={1}
          direction={forceMobile ? 'column' : { base: 'column', md: 'row' }}>
          {!isTablet && !forceMobile && <Stack flex={{ base: 0, lg: 1 }} />}
          <ChannelSelection
            onValueChange={setChannelSelected}
            selectedValue={channelSelected}
            setChannelAuditSelected={setChannelAuditSelected}
            forceMobile={forceMobile}
          />
          {!hideDateTimeFields && (
            <DateSelection
              onValueChange={setDateSelected}
              selectedValue={dateSelected}
              forceMobile={forceMobile}
            />
          )}
          {!hideDateTimeFields && (
            <TimeSelection
              onValueChange={setTimeSelected}
              selectedValue={timeSelected}
              forceMobile={forceMobile}
            />
          )}
        </Stack>
      </Stack>
      <HStack
        space="inline.xxs"
        pt="stack.xxxs"
        justifyContent="space-between"
        alignItems="center"
        flex={1}>
        <AgreementCheck
          onChange={setAgreementChecked}
          isChecked={agreementChecked}
          unableToUncheck={!!defaultInteractionChannel}
        />
        {CustomHandleElement}
      </HStack>
      {!hideSeparator && (
        <Box
          mt="stack.xxs"
          borderTopWidth="1px"
          borderColor="neutral.lightest"
          borderStyle="dashed"
        />
      )}
    </VStack>
  );
};
